import * as React from "react"
import { useNavigate } from "react-router-dom"
import { motion } from "motion/react"
import {
  ArrowRight,
  Building2,
  CheckCircle2,
  CircleAlert,
  Clock3,
  Eye,
  Loader2,
  Mail,
  Search,
  Shield,
  Sparkles,
  User,
} from "lucide-react"
import { AdminCard } from "./AdminCard"
import { useAuth, type AdminCustomerSearchResult } from "@/contexts/AuthContext"

type SearchFeedback = {
  type: "error" | "info"
  text: string
} | null

const userTypeLabel = (userType: "agent" | "solo_agent" | "agency" | null) => {
  if (userType === "solo_agent") return "Solo Agent"
  if (userType === "agent") return "Agent"
  if (userType === "agency") return "Agency"
  return "Unstarted"
}

const formatCreatedAt = (value: string) => {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return "Unknown join date"
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(date)
}

export function AdminSupport() {
  const navigate = useNavigate()
  const { user, investigation, searchAdminUsers, startInvestigateMode } = useAuth()

  const [customerQuery, setCustomerQuery] = React.useState("")
  const [customerSearchResults, setCustomerSearchResults] = React.useState<AdminCustomerSearchResult[]>([])
  const [customerSearchFeedback, setCustomerSearchFeedback] = React.useState<SearchFeedback>(null)
  const [isSearchingCustomers, setIsSearchingCustomers] = React.useState(false)
  const [investigatingUserId, setInvestigatingUserId] = React.useState<string | null>(null)
  const [selectedCustomerId, setSelectedCustomerId] = React.useState<string | null>(null)

  const selectedCustomer =
    customerSearchResults.find((customer) => customer._id === selectedCustomerId) || customerSearchResults[0] || null

  const isSupportOperator = user?.role === "support"

  const handleCustomerSearch = async () => {
    const query = customerQuery.trim()
    if (query.length < 2) {
      setCustomerSearchResults([])
      setSelectedCustomerId(null)
      setCustomerSearchFeedback({ type: "error", text: "Enter at least 2 characters (name or email)." })
      return
    }

    setIsSearchingCustomers(true)
    setCustomerSearchFeedback(null)

    const response = await searchAdminUsers(query)
    if (response.success) {
      const users = response.users || []
      setCustomerSearchResults(users)
      setSelectedCustomerId((prev) => (users.some((customer) => customer._id === prev) ? prev : (users[0]?._id ?? null)))
      setCustomerSearchFeedback(
        users.length === 0
          ? { type: "info", text: "No matching customers found. Try a different name, email, or a shorter search phrase." }
          : { type: "info", text: `Found ${users.length} customer${users.length === 1 ? "" : "s"}. Select one to launch Investigate Mode.` }
      )
    } else {
      setCustomerSearchResults([])
      setSelectedCustomerId(null)
      setCustomerSearchFeedback({ type: "error", text: response.message || "Failed to search customers." })
    }

    setIsSearchingCustomers(false)
  }

  const handleStartInvestigate = async (targetUserId?: string) => {
    if (!targetUserId) {
      setCustomerSearchFeedback({ type: "error", text: "Select a customer before starting Investigate Mode." })
      return
    }

    setInvestigatingUserId(targetUserId)
    setCustomerSearchFeedback(null)

    const response = await startInvestigateMode(targetUserId)
    if (response.success) {
      navigate("/dashboard", { replace: true })
      return
    }

    setCustomerSearchFeedback({ type: "error", text: response.message || "Failed to start investigate mode." })
    setInvestigatingUserId(null)
  }

  return (
    <div className="space-y-6">
      <AdminCard className="relative overflow-hidden border-[#2A2A45] bg-[linear-gradient(180deg,#12111A_0%,#0E0D13_100%)]">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-16 right-12 h-40 w-40 rounded-full bg-[#F37020]/12 blur-3xl" />
          <div className="absolute bottom-0 left-8 h-24 w-56 rounded-full bg-white/[0.03] blur-2xl" />
          <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#F59A58]/40 to-transparent" />
        </div>

        <div className="relative flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-[#F37020]/25 bg-[#F37020]/10 px-3 py-1 text-[11px] uppercase tracking-[0.18em] text-[#F6A56E] font-admin">
              <Sparkles className="h-3.5 w-3.5" />
              Support Console
            </div>
            <h3 className="mt-3 text-xl md:text-2xl font-semibold text-white font-admin">
              Lookup customers fast. Investigate with confidence.
            </h3>
            <p className="mt-2 text-sm text-white/50 font-admin leading-relaxed">
              This workspace is optimized for support triage. Find a customer, verify account context, then open Investigate
              Mode to experience the product exactly as they do.
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-3 py-1.5 text-xs text-white/70 font-admin">
              <Shield className="h-3.5 w-3.5 text-[#F59A58]" />
              {isSupportOperator ? "Support Operator" : "Super Admin"}
            </span>
            <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-3 py-1.5 text-xs text-white/70 font-admin">
              <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" />
              Customer-only investigate sessions
            </span>
          </div>
        </div>
      </AdminCard>

      {investigation?.isActive && (
        <AdminCard className="border-[#F37020]/25 bg-[#120E09]">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-[#F6A56E] font-admin">Investigate Session Active</p>
              <p className="mt-1 text-sm text-white/80 font-admin">
                You are currently viewing as <span className="text-white font-semibold">{user?.email || "customer"}</span>.
              </p>
            </div>
            <p className="text-xs text-white/45 font-admin">
              Use the floating banner&apos;s <span className="text-white/70">End now</span> action to return to admin mode.
            </p>
          </div>
        </AdminCard>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-[1.12fr_0.88fr] gap-6 items-start">
        <AdminCard delay={0.05} className="relative overflow-hidden">
          <div className="absolute inset-x-5 top-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />

          <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
            <div>
              <h3 className="text-sm font-semibold text-white/85 font-admin">1. User Lookup</h3>
              <p className="text-xs text-white/35 mt-1 font-admin">
                Search by name or email. Results include agent, solo agent, and agency customers.
              </p>
            </div>
            <div className="text-[11px] text-white/25 font-admin">
              {customerSearchResults.length > 0 ? `${customerSearchResults.length} result${customerSearchResults.length === 1 ? "" : "s"}` : "No results yet"}
            </div>
          </div>

          <div className="mt-4 flex flex-col gap-3 sm:flex-row">
            <label className="flex-1 space-y-1 text-xs text-white/40 font-admin">
              Name or Email
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/20" />
                <input
                  value={customerQuery}
                  onChange={(e) => setCustomerQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault()
                      if (!isSearchingCustomers && !investigatingUserId) {
                        handleCustomerSearch()
                      }
                    }
                  }}
                  placeholder="Jane Doe or jane@agency.com"
                  className="w-full rounded-xl border border-[#2A2A45] bg-[#0C0C12] pl-9 pr-3 py-2.5 text-sm text-white/75 placeholder:text-white/20 outline-none transition-colors focus:border-[#F37020]/45 font-admin"
                  disabled={isSearchingCustomers || !!investigatingUserId}
                />
              </div>
            </label>

            <button
              type="button"
              onClick={handleCustomerSearch}
              disabled={isSearchingCustomers || !!investigatingUserId}
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-[#2A2A45] bg-white/[0.03] px-4 py-2.5 text-sm text-white/75 hover:text-white hover:border-[#F37020]/35 hover:bg-[#F37020]/[0.06] transition-colors disabled:opacity-60 font-admin"
            >
              {isSearchingCustomers ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              {isSearchingCustomers ? "Searching" : "Search"}
            </button>
          </div>

          <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] font-admin">
            <span className="rounded-full border border-white/10 bg-white/[0.03] px-2.5 py-1 text-white/45">Press Enter to search</span>
            <span className="rounded-full border border-white/10 bg-white/[0.03] px-2.5 py-1 text-white/45">Customers only</span>
            <span className="rounded-full border border-white/10 bg-white/[0.03] px-2.5 py-1 text-white/45">Backend permissions enforced</span>
          </div>

          {customerSearchFeedback && (
            <div
              className={`mt-4 rounded-xl border px-3 py-2.5 text-xs font-admin flex items-start gap-2 ${
                customerSearchFeedback.type === "error"
                  ? "border-red-500/25 bg-red-500/10 text-red-200"
                  : "border-white/10 bg-white/[0.03] text-white/55"
              }`}
            >
              {customerSearchFeedback.type === "error" ? (
                <CircleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              ) : (
                <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-400" />
              )}
              <span>{customerSearchFeedback.text}</span>
            </div>
          )}

          <div className="mt-4">
            {customerSearchResults.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-[#2A2A45] bg-[#0C0C12]/70 px-4 py-8 text-center">
                <p className="text-sm text-white/55 font-admin">Search for a customer to begin.</p>
                <p className="text-xs text-white/25 mt-1 font-admin">We will surface matching users with account state and agency context.</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-[560px] overflow-y-auto pr-1">
                {customerSearchResults.map((customer, index) => {
                  const isSelected = selectedCustomer?._id === customer._id
                  const isLaunching = investigatingUserId === customer._id

                  return (
                    <motion.button
                      key={customer._id}
                      type="button"
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.2, delay: index * 0.02 }}
                      onClick={() => setSelectedCustomerId(customer._id)}
                      disabled={!!investigatingUserId}
                      className={`w-full text-left rounded-2xl border px-4 py-3 transition-colors ${
                        isSelected
                          ? "border-[#F37020]/40 bg-[#F37020]/10"
                          : "border-[#2A2A45]/70 bg-[#0C0C12] hover:border-[#F37020]/20 hover:bg-white/[0.02]"
                      } disabled:opacity-70`}
                    >
                      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="text-sm text-white/90 font-admin">{customer.displayName}</span>
                            <span className="rounded-full border border-[#F37020]/20 bg-[#F37020]/10 px-2 py-0.5 text-[10px] uppercase tracking-wider text-[#F59A58] font-admin">
                              {userTypeLabel(customer.userType)}
                            </span>
                            {customer.emailVerified ? (
                              <span className="rounded-full border border-emerald-400/20 bg-emerald-400/10 px-2 py-0.5 text-[10px] uppercase tracking-wider text-emerald-300 font-admin">
                                Verified
                              </span>
                            ) : (
                              <span className="rounded-full border border-amber-400/20 bg-amber-400/10 px-2 py-0.5 text-[10px] uppercase tracking-wider text-amber-300 font-admin">
                                Unverified
                              </span>
                            )}
                            {!customer.hasCompletedOnboarding && (
                              <span className="rounded-full border border-white/10 bg-white/[0.04] px-2 py-0.5 text-[10px] uppercase tracking-wider text-white/45 font-admin">
                                Onboarding
                              </span>
                            )}
                          </div>

                          <div className="mt-2 flex flex-col gap-1 text-xs text-white/40 font-admin">
                            <div className="flex items-center gap-2 min-w-0">
                              <Mail className="h-3.5 w-3.5 shrink-0" />
                              <span className="truncate">{customer.email}</span>
                            </div>
                            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-white/30">
                              <span className="inline-flex items-center gap-1.5">
                                <Building2 className="h-3.5 w-3.5" />
                                {customer.agencyName || "No agency assigned"}
                              </span>
                              <span className="inline-flex items-center gap-1.5">
                                <Clock3 className="h-3.5 w-3.5" />
                                Joined {formatCreatedAt(customer.createdAt)}
                              </span>
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center justify-between gap-2 shrink-0">
                          <span
                            className={`text-[11px] uppercase tracking-wider font-admin ${
                              isSelected ? "text-[#F59A58]" : "text-white/25"
                            }`}
                          >
                            {isLaunching ? "Launching..." : isSelected ? "Selected" : "Select"}
                          </span>
                          <ArrowRight className={`h-4 w-4 ${isSelected ? "text-[#F59A58]" : "text-white/25"}`} />
                        </div>
                      </div>
                    </motion.button>
                  )
                })}
              </div>
            )}
          </div>
        </AdminCard>

        <div className="space-y-6">
          <AdminCard delay={0.1} className="relative overflow-hidden border-[#2A2A45]">
            <div className="absolute -right-6 -top-10 h-28 w-28 rounded-full bg-[#F37020]/10 blur-2xl" />

            <h3 className="text-sm font-semibold text-white/85 font-admin">2. Investigate Mode</h3>
            <p className="text-xs text-white/35 mt-1 font-admin">
              This opens the customer session in the main dashboard so you can reproduce exactly what they see.
            </p>

            <div className="mt-4 space-y-3">
              <div className="rounded-xl border border-white/10 bg-[#0C0C12] px-3 py-3">
                <p className="text-[11px] uppercase tracking-wider text-white/30 font-admin">Flow</p>
                <div className="mt-2 space-y-2 text-sm font-admin">
                  <div className="flex items-start gap-2 text-white/70">
                    <span className="mt-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full border border-white/10 bg-white/[0.03] text-[11px]">1</span>
                    Search and select the customer you need to support
                  </div>
                  <div className="flex items-start gap-2 text-white/70">
                    <span className="mt-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full border border-white/10 bg-white/[0.03] text-[11px]">2</span>
                    Review their account type, verification, onboarding, and agency context
                  </div>
                  <div className="flex items-start gap-2 text-white/70">
                    <span className="mt-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full border border-[#F37020]/25 bg-[#F37020]/10 text-[11px] text-[#F59A58]">3</span>
                    Launch Investigate Mode and use the top banner to end the session when done
                  </div>
                </div>
              </div>

              <div className="rounded-xl border border-[#F37020]/20 bg-[#130F0C] px-3 py-3">
                <p className="text-xs text-[#F7B182] font-admin leading-relaxed">
                  Investigate Mode keeps support work fast and safe: backend permissions still apply, and customer access is limited to
                  approved investigate sessions.
                </p>
              </div>
            </div>
          </AdminCard>

          <AdminCard delay={0.15} className="relative overflow-hidden border-[#2A2A45]">
            <div className="absolute inset-x-5 top-0 h-px bg-gradient-to-r from-transparent via-[#F37020]/20 to-transparent" />

            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="text-sm font-semibold text-white/85 font-admin">Selected Customer</h3>
                <p className="text-xs text-white/35 mt-1 font-admin">Confirm identity, then start the investigate session.</p>
              </div>
              <div className="rounded-full border border-white/10 bg-white/[0.03] px-2.5 py-1 text-[10px] uppercase tracking-wider text-white/40 font-admin">
                {selectedCustomer ? "Ready" : "Waiting"}
              </div>
            </div>

            {selectedCustomer ? (
              <div className="mt-4 space-y-4">
                <div className="rounded-2xl border border-[#2A2A45]/70 bg-[#0C0C12] p-4">
                  <div className="flex items-start gap-3">
                    <div className="h-11 w-11 rounded-xl border border-[#F37020]/20 bg-[#F37020]/10 flex items-center justify-center shrink-0">
                      {selectedCustomer.userType === "agency" ? (
                        <Building2 className="h-5 w-5 text-[#F59A58]" />
                      ) : (
                        <User className="h-5 w-5 text-[#F59A58]" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-white/90 font-admin">{selectedCustomer.displayName}</p>
                      <p className="text-xs text-white/40 mt-1 break-all font-admin">{selectedCustomer.email}</p>
                      <div className="mt-2 flex flex-wrap gap-2">
                        <span className="rounded-full border border-[#F37020]/20 bg-[#F37020]/10 px-2 py-0.5 text-[10px] uppercase tracking-wider text-[#F59A58] font-admin">
                          {userTypeLabel(selectedCustomer.userType)}
                        </span>
                        <span
                          className={`rounded-full px-2 py-0.5 text-[10px] uppercase tracking-wider font-admin border ${
                            selectedCustomer.emailVerified
                              ? "border-emerald-400/20 bg-emerald-400/10 text-emerald-300"
                              : "border-amber-400/20 bg-amber-400/10 text-amber-300"
                          }`}
                        >
                          {selectedCustomer.emailVerified ? "Email Verified" : "Email Unverified"}
                        </span>
                        <span
                          className={`rounded-full px-2 py-0.5 text-[10px] uppercase tracking-wider font-admin border ${
                            selectedCustomer.hasCompletedOnboarding
                              ? "border-white/10 bg-white/[0.04] text-white/55"
                              : "border-white/10 bg-white/[0.04] text-white/35"
                          }`}
                        >
                          {selectedCustomer.hasCompletedOnboarding ? "Onboarding Complete" : "Onboarding In Progress"}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="rounded-xl border border-white/10 bg-white/[0.02] px-3 py-2">
                      <p className="text-[10px] uppercase tracking-wider text-white/25 font-admin">Agency</p>
                      <p className="mt-1 text-sm text-white/75 font-admin">{selectedCustomer.agencyName || "No agency assigned"}</p>
                    </div>
                    <div className="rounded-xl border border-white/10 bg-white/[0.02] px-3 py-2">
                      <p className="text-[10px] uppercase tracking-wider text-white/25 font-admin">Joined</p>
                      <p className="mt-1 text-sm text-white/75 font-admin">{formatCreatedAt(selectedCustomer.createdAt)}</p>
                    </div>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => handleStartInvestigate(selectedCustomer._id)}
                  disabled={isSearchingCustomers || !!investigatingUserId}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-2xl border border-[#F37020]/30 bg-[#F37020]/15 px-4 py-3 text-sm font-semibold text-[#FDB37E] hover:bg-[#F37020]/25 transition-colors disabled:opacity-60 font-admin"
                >
                  {investigatingUserId === selectedCustomer._id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                  {investigatingUserId === selectedCustomer._id ? "Starting Investigate Mode..." : "Open Investigate Session"}
                </button>
              </div>
            ) : (
              <div className="mt-4 rounded-2xl border border-dashed border-[#2A2A45] bg-[#0C0C12]/70 px-4 py-8 text-center">
                <p className="text-sm text-white/55 font-admin">No customer selected.</p>
                <p className="text-xs text-white/25 mt-1 font-admin">Run a search and pick a result to unlock the investigate action.</p>
              </div>
            )}
          </AdminCard>
        </div>
      </div>
    </div>
  )
}
